import { useState, type ChangeEvent } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertTriangle, IconBuilding, IconSend, IconSparkles } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type {
  CustomerDuplicateMatchDto,
  CustomerProfileCandidateDto,
  CustomerProfileResearchResult,
} from '../lib/api/domains/customerRequests';
import {
  EMPTY_DRAFT,
  applyCandidate,
  editDraft,
  notesWithSources,
  researchHints,
  type DraftField,
  type ProposeCustomerDraft,
} from '../lib/proposeCustomerDraft';

/**
 * „Neuen Kunden vorschlagen" — Techniker füllen die Stammdaten aus, Admins bekommen
 * Mail + Glocke + Push und geben in der Warteschlange (Admin → Kundenanlagen) frei.
 * Die Freigabe legt den Kunden im ServiceHub an (Kundennummer vergibt der ServiceHub)
 * und schickt ihn danach nach weclapp.
 *
 * Name eintippen, „Von KIAN ausfüllen": KIAN sucht Impressum und Webseite und füllt die
 * leeren Felder (Regeln: `lib/proposeCustomerDraft.ts`). Mehrere passende Firmen stehen
 * zur Auswahl; gibt es den Kunden schon, steht das vor dem Einreichen da.
 */
export interface ProposeCustomerPrefill {
  companyName?: string;
  contactName?: string;
  email?: string;
  notes?: string;
}

const CONFIDENCE_LABEL: Record<string, { label: string; color: string }> = {
  high: { label: 'sicher', color: 'green' },
  medium: { label: 'wahrscheinlich', color: 'yellow' },
  low: { label: 'unsicher', color: 'orange' },
};

export function ProposeCustomerButton({
  prefill,
  buttonLabel = 'Neuen Kunden vorschlagen',
  size = 'xs',
}: {
  /** Vorbefüllung z. B. aus einem Ticket ohne Kunden (Absender-Mail, Domain-Firmenname). */
  prefill?: ProposeCustomerPrefill;
  buttonLabel?: string;
  size?: string;
} = {}) {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<ProposeCustomerDraft>(EMPTY_DRAFT);
  const [research, setResearch] = useState<CustomerProfileResearchResult | null>(null);
  const [applied, setApplied] = useState<CustomerProfileCandidateDto | null>(null);
  const { values, kian } = draft;
  // Am Handy bliebe neben „Von KIAN ausfüllen" kaum Platz für den Namen.
  const narrow = useMediaQuery('(max-width: 36em)');

  const reset = () => {
    setDraft(EMPTY_DRAFT);
    setResearch(null);
    setApplied(null);
  };

  const pick = (candidate: CustomerProfileCandidateDto) => {
    setDraft((d) => applyCandidate(d, candidate));
    setApplied(candidate);
  };

  const researchMutation = useMutation({
    mutationFn: () => api.researchCustomerProfile(researchHints(draft)),
    onSuccess: (result) => {
      setResearch(result);
      setApplied(null);
      // Ein eindeutiger Treffer wird gleich übernommen; bei mehreren wählt der Techniker.
      if (result.candidates.length === 1) pick(result.candidates[0]);
    },
    onError: (e: unknown) => {
      const message = e instanceof ApiError ? e.summary || e.message : e instanceof Error ? e.message : 'Unbekannter Fehler';
      notifications.show({ title: 'Recherche fehlgeschlagen', message, color: 'red' });
    },
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.createCustomerRequest({
        companyName: values.companyName.trim(),
        contactName: values.contactName.trim() || undefined,
        email: values.email.trim() || undefined,
        phone: values.phone.trim() || undefined,
        street: values.street.trim() || undefined,
        zipCode: values.zipCode.trim() || undefined,
        city: values.city.trim() || undefined,
        website: values.website.trim() || undefined,
        notes: notesWithSources(values.notes, applied) || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-requests'] });
      setOpened(false);
      reset();
      notifications.show({
        title: 'Vorschlag eingereicht',
        message: 'Die Admins wurden benachrichtigt — nach der Freigabe bekommst du eine Mail mit dem Kunden-Link.',
        color: 'teal',
      });
    },
    onError: (e: unknown) => {
      const message =
        e instanceof ApiError ? e.summary || e.message : e instanceof Error ? e.message : 'Unbekannter Fehler';
      notifications.show({ title: 'Vorschlag fehlgeschlagen', message, color: 'red' });
    },
  });

  const canResearch = values.companyName.trim().length >= 2 && !researchMutation.isPending;
  const startResearch = () => {
    if (canResearch) researchMutation.mutate();
  };

  /** Feld mit KIAN-Hinweis darunter, solange der Wert von KIAN stammt. */
  const field = (name: DraftField) => ({
    value: values[name],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.currentTarget.value;
      setDraft((d) => editDraft(d, name, value));
    },
    description: kian[name] ? (
      <Text span size="xs" c="violet">
        {kian[name]}
      </Text>
    ) : undefined,
    inputWrapperOrder: ['label', 'input', 'description', 'error'] as ('label' | 'input' | 'description' | 'error')[],
  });

  const duplicate = applied ? applied.duplicate : (research?.duplicate ?? null);
  const choosing = !!research && research.candidates.length > 1 && !applied;

  return (
    <>
      <Button
        size={size}
        variant="light"
        leftSection={<IconBuilding size={14} />}
        onClick={() => {
          // Vorbefüllung nur beim Öffnen und nur in leere Felder — bereits
          // getippte Eingaben eines früheren Öffnens bleiben erhalten.
          if (prefill) {
            setDraft((d) => {
              let next = d;
              for (const key of ['companyName', 'contactName', 'email', 'notes'] as const) {
                const v = prefill[key];
                if (v && !next.values[key]) next = editDraft(next, key, v);
              }
              return next;
            });
          }
          setOpened(true);
        }}
      >
        {buttonLabel}
      </Button>

      {/* zIndex über den Kopf-Popovers des Cockpits (zIndex+100) — sonst liegt das
          Modal hinter dem „Kunde zuordnen"-Popover, aus dem es geöffnet wurde. */}
      <Modal opened={opened} onClose={() => setOpened(false)} title="Neuen Kunden vorschlagen" size="lg" centered zIndex={950}>
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            Der Vorschlag geht an die Admins. Nach der Freigabe legt der ServiceHub den Kunden an
            (mit Kundennummer) und du bekommst eine Mail mit Deeplink.
          </Text>
          <Group align="flex-end" gap="xs" wrap="nowrap">
            <TextInput
              label="Firmenname"
              required
              style={{ flex: 1 }}
              placeholder="z. B. Praxis Dr. Beispiel Unna"
              {...field('companyName')}
              // Hinweis steht unter der Zeile — sonst stünde der Knopf nicht mehr auf Höhe des Felds.
              description={undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  startResearch();
                }
              }}
            />
            <Button
              variant="light"
              color="violet"
              leftSection={<IconSparkles size={14} />}
              loading={researchMutation.isPending}
              disabled={values.companyName.trim().length < 2}
              onClick={startResearch}
              style={{ flexShrink: 0 }}
            >
              {narrow ? 'KIAN' : 'Von KIAN ausfüllen'}
            </Button>
          </Group>
          {kian.companyName && (
            <Text size="xs" c="violet" mt={-6}>
              {kian.companyName}
            </Text>
          )}
          {!research && !researchMutation.isPending && (
            <Text size="xs" c="dimmed" mt={-6}>
              Name eintippen, gern mit Ort — KIAN sucht Anschrift, Telefon, Mail und Webseite und füllt die
              leeren Felder. Was du schon eingetragen hast, bleibt stehen.
            </Text>
          )}
          {researchMutation.isPending && (
            <Text size="xs" c="dimmed" mt={-6}>
              KIAN sucht Impressum und Webseite … das dauert bis zu einer halben Minute.
            </Text>
          )}

          {research && (
            <ResearchNotice
              result={research}
              applied={applied}
              choosing={choosing}
              onPick={pick}
              onChooseAgain={() => setApplied(null)}
            />
          )}
          {duplicate && !choosing && <DuplicateNotice match={duplicate} onNavigate={() => setOpened(false)} />}

          <Group grow align="flex-start">
            <TextInput label="Ansprechpartner" {...field('contactName')} />
            <TextInput label="E-Mail" type="email" {...field('email')} />
          </Group>
          <Group grow align="flex-start">
            <TextInput label="Telefon" {...field('phone')} />
            <TextInput label="Website" {...field('website')} />
          </Group>
          <TextInput label="Straße" {...field('street')} />
          <Group grow align="flex-start">
            <TextInput label="PLZ" {...field('zipCode')} />
            <TextInput label="Ort" {...field('city')} />
          </Group>
          <Textarea
            label="Notiz an die Admins (optional)"
            {...field('notes')}
            autosize
            minRows={2}
            placeholder="Kontext: woher kommt der Kunde, was ist geplant?"
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setOpened(false)}>Abbrechen</Button>
            <Button
              leftSection={<IconSend size={14} />}
              disabled={values.companyName.trim().length < 2 || researchMutation.isPending}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Vorschlag einreichen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function safeHref(source: string): string | null {
  try {
    const url = new URL(source);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function candidateAddress(c: Pick<CustomerProfileCandidateDto, 'street' | 'zipCode' | 'city'>): string {
  return [c.street, [c.zipCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

function ResearchNotice({
  result,
  applied,
  choosing,
  onPick,
  onChooseAgain,
}: {
  result: CustomerProfileResearchResult;
  applied: CustomerProfileCandidateDto | null;
  choosing: boolean;
  onPick: (c: CustomerProfileCandidateDto) => void;
  onChooseAgain: () => void;
}) {
  if (result.candidates.length === 0) {
    return (
      <Alert color={result.available ? 'yellow' : 'gray'} variant="light" icon={<IconSparkles size={16} />}>
        <Text size="xs">
          {result.remark || 'KIAN hat nichts Passendes gefunden.'} Tipp: Ort oder Mail-Adresse dazuschreiben — oder
          von Hand ausfüllen.
        </Text>
      </Alert>
    );
  }

  if (choosing) {
    return (
      <Alert color="violet" variant="light" icon={<IconSparkles size={16} />}>
        <Stack gap={6}>
          <Text size="xs" fw={600}>
            KIAN hat mehrere passende Firmen gefunden — welche ist gemeint?
          </Text>
          {result.candidates.map((c, i) => (
            <UnstyledButton key={`${c.companyName}-${i}`} onClick={() => onPick(c)}>
              <Paper withBorder p="xs" radius="sm">
                <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600}>
                      {c.companyName}
                    </Text>
                    {candidateAddress(c) && (
                      <Text size="xs" c="dimmed">
                        {candidateAddress(c)}
                      </Text>
                    )}
                    {c.description && <Text size="xs">{c.description}</Text>}
                  </div>
                  {c.duplicate && (
                    <Badge size="xs" color="orange" variant="light" style={{ flexShrink: 0 }}>
                      gibt es schon
                    </Badge>
                  )}
                </Group>
              </Paper>
            </UnstyledButton>
          ))}
          {result.remark && (
            <Text size="xs" c="dimmed">
              {result.remark}
            </Text>
          )}
        </Stack>
      </Alert>
    );
  }

  if (!applied) return null;
  const confidence = result.confidence ? CONFIDENCE_LABEL[result.confidence] : null;
  return (
    <Alert color="violet" variant="light" icon={<IconSparkles size={16} />}>
      <Stack gap={4}>
        <Group gap="xs">
          <Text size="xs" fw={600}>
            Von KIAN ausgefüllt — bitte prüfen, bevor du einreichst.
          </Text>
          {confidence && (
            <Badge size="xs" variant="light" color={confidence.color}>
              {confidence.label}
            </Badge>
          )}
        </Group>
        {result.remark && <Text size="xs">{result.remark}</Text>}
        {applied.sources.length > 0 && (
          <Text size="xs" c="dimmed">
            Quellen (gehen mit an die Admins):{' '}
            {applied.sources.map((source, i) => {
              const href = safeHref(source);
              return (
                <span key={source}>
                  {i > 0 && ', '}
                  {href ? (
                    <Anchor href={href} target="_blank" rel="noopener noreferrer nofollow" size="xs">
                      {new URL(href).hostname}
                    </Anchor>
                  ) : (
                    source
                  )}
                </span>
              );
            })}
          </Text>
        )}
        {result.candidates.length > 1 && (
          <Anchor component="button" type="button" size="xs" onClick={onChooseAgain} style={{ alignSelf: 'flex-start' }}>
            Andere Firma wählen ({result.candidates.length} Treffer)
          </Anchor>
        )}
      </Stack>
    </Alert>
  );
}

function DuplicateNotice({ match, onNavigate }: { match: CustomerDuplicateMatchDto; onNavigate: () => void }) {
  const href = match.weclappPartyId
    ? `/customer-hub/customer?weclappCustomerId=${encodeURIComponent(match.weclappPartyId)}`
    : match.localCustomerId
      ? `/customer-hub?customerId=${encodeURIComponent(match.localCustomerId)}`
      : null;
  return (
    <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
      <Stack gap={2}>
        <Text size="xs" fw={600}>
          Diesen Kunden gibt es {match.confidence === 'high' ? 'vermutlich' : 'vielleicht'} schon:{' '}
          {match.name}
          {match.customerNumber ? ` (Kd.-Nr. ${match.customerNumber})` : ''}
        </Text>
        <Text size="xs">
          {match.detail.charAt(0).toUpperCase() + match.detail.slice(1)}. Ein doppelter Vorschlag wird bei der
          Freigabe als Duplikat geschlossen.
        </Text>
        {href && (
          <Anchor component={Link} to={href} size="xs" onClick={onNavigate} style={{ alignSelf: 'flex-start' }}>
            Kundenakte öffnen
          </Anchor>
        )}
      </Stack>
    </Alert>
  );
}
